import { CurrencyPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Card, ICONS } from 'src/app/types';
import { DriverAverageRevenue, LohiDashboardService } from '../lohi-dashboard.service';
import { UpdateMonthlyCostsModalComponent } from '../update-costs-modal/update-costs-modal.component';

const CURRENT_INTERVAL = 7; // Todo: make it dynamic when there's a dropdown

@Component({
  selector: 'lohi-app-performance',
  templateUrl: './performance.component.html',
  styleUrls: ['./performance.component.scss'],
  providers: [CurrencyPipe],
})
export class PerformanceComponent implements OnInit {
  constructor(
    private currencyPipe: CurrencyPipe,
    public dialog: MatDialog,
    private dashboardService: LohiDashboardService,
  ) {}

  public showAll = false;
  public cards: any[] = [];
  public costs: { label: string; value: string; link?: string }[] = [];

  public additionalCards: any[] = [];
  public driverRevenues: any[] = [];
  public totalDriverRevenues = '';
  public activeTab = 'profit';
  public currentTab: 'avg-daily-profit' | 'lost-revenue-by-driver-mistake' = 'avg-daily-profit';

  public ngOnInit(): void {
    this.initializeCards();
    this.fetchDriverAverageCosts();

    this.dashboardService.updateEvent$.subscribe(() => {
      this.fetchDriverAverageCosts();
    });
  }

  private getDefaultCardProps(): Omit<Card, 'title'> {
    return {
      value: '-',
      subtext: 'Past 7 days',
      infoIcon: ICONS.information,
      indicatorIcon: '',
      indicatorText: '',
      textColor: '',
      indicatorClass: '',
    };
  }

  private initializeCards(): void {
    this.cards = [
      { title: 'Total revenue', ...this.getDefaultCardProps() },
      { title: 'Missed Revenue', ...this.getDefaultCardProps() },
      { title: 'Average turn rate', ...this.getDefaultCardProps() },
    ];

    this.additionalCards = [
      { title: 'On-time performance', ...this.getDefaultCardProps() },
      { title: 'Total loads rejected', ...this.getDefaultCardProps() },
      { title: 'Total Ran Loads', ...this.getDefaultCardProps() },
    ];

    this.dashboardService.getRevenue$().subscribe({
      next: (response) => {
        const formattedRevenue = this.currencyPipe.transform(response.totalRevenueCent / 100, 'USD', 'symbol', '1.2-2');
        this.updateCard(
          0,
          formattedRevenue || '-',
          response.rank.arrowDirection
            ? response.rank.arrowDirection === 'up'
              ? ICONS.upArrowGreen
              : ICONS.bottomArrowRed
            : '',
          response.rank.rankText || '',
          response.rank.color === 'green' ? 'text-green-600' : 'text-red-500',
          response.rank.color ? 'text-lightGreen' : 'text-red-500',
        );
      },
      error: () => {
        this.updateCardError(0);
      },
    });

    this.dashboardService.getLostRevenueByDriver$().subscribe({
      next: (response) => {
        const sumAllDriverOverallMissedRevenue = response.reduce((acc, driver) => acc + driver.overallMissedRevenue, 0);
        const formattedMissedRevenue = this.currencyPipe.transform(
          sumAllDriverOverallMissedRevenue / 100,
          'USD',
          'symbol',
          '1.2-2',
        );
        this.updateCard(1, formattedMissedRevenue || '-', '', '', '', '');
      },
      error: () => {
        this.updateCardError(1);
      },
    });

    this.dashboardService.getAverageTurnRate$().subscribe({
      next: (response) => {
        this.updateCard(2, response.average_turn_rate?.toFixed(1) || '-', '', '', '', '');
      },
      error: () => {
        this.updateCardError(2);
      },
    });

    this.dashboardService.getOnTimePerformance$().subscribe({
      next: (response) => {
        this.updateAdditionalCard(
          0,
          response.onTimePerformance ? `${response.onTimePerformance}%` : '-',
          response.rank.arrowDirection
            ? response.rank.arrowDirection === 'up'
              ? ICONS.upArrowGreen
              : ICONS.bottomArrowRed
            : '',
          response.rank.rankText || '',
          response.rank.arrowDirection === 'up' ? 'text-green-600' : 'text-red-500',
          response.rank.arrowDirection === 'up' ? 'text-lightGreen' : 'text-red-500',
        );
      },
      error: () => {
        this.updateCardError(0);
      },
    });

    this.dashboardService.getTotalRejectedLoads$().subscribe({
      next: (response) => {
        this.updateAdditionalCard(1, `${response}` || '-', '', '', '', '');
      },
      error: () => {
        this.updateAdditionalCardError(1);
      },
    });

    this.dashboardService.getTotalRanLoads$().subscribe({
      next: (response) => {
        this.updateAdditionalCard(2, `${response}` || '-', '', '', '', '');
      },
      error: () => {
        this.updateAdditionalCardError(2);
      },
    });

    this.dashboardService.getDriverRevenue$().subscribe({
      next: (response) => {
        this.driverRevenues = response;
        const totalDriverProfit = response.reduce((acc, driver) => acc + (driver.totalProfit || 0), 0);
        this.totalDriverRevenues = this.currencyPipe.transform(totalDriverProfit / 7 / 100, 'USD', 'symbol', '1.2-2');
        this.driverRevenues.sort((a, b) => b.totalRevenue - a.totalRevenue);
        this.driverRevenues.forEach((driver) => {
          const dailyRevenue = (driver.totalRevenue || 0) / 7;
          const dailyProfit = (driver.totalProfit || 0) / 7;
          const dailyCost = (driver.totalCost || 0) / 7;

          driver.totalProfit = this.currencyPipe.transform(dailyProfit / 100, 'USD', 'symbol', '1.2-2');
          driver.totalRevenue = this.currencyPipe.transform(dailyRevenue / 100, 'USD', 'symbol', '1.2-2');
          driver.totalCost = this.currencyPipe.transform(dailyCost / 100, 'USD', 'symbol', '1.2-2');
        });
      },
    });
  }

  private fetchDriverAverageCosts(): void {
    this.dashboardService.getDriverAverageCosts$(CURRENT_INTERVAL).subscribe({
      next: (data: DriverAverageRevenue) => {
        this.costs = [
          {
            label: 'Fixed Costs',
            value: this.formatCentsToDollars(data.fixedCostCentsPerDay),
            link: '#',
          },
          {
            label: 'Average Fuel',
            value: this.formatCentsToDollars(data.averageFuelCostCentsPerDay),
          },
          {
            label: 'Average Insurance',
            value: this.formatCentsToDollars(data.averageInsuranceCostCentsPerDay),
          },
          {
            label: 'Average Maintenance',
            value: this.formatCentsToDollars(data.averageMaintenanceCostCentsPerDay),
          },
          {
            label: 'Average Take Rate',
            value: this.formatCentsToDollars(data.averageTakeRateCentsPerDay),
          },
        ];
      },
      error: (err) => {
        console.error('Error fetching driver average costs:', err);
        this.costs = [];
      },
    });
  }

  public switchTab(index: number): void {
    const tabMapping = ['avg-daily-profit', 'lost-revenue-by-driver-mistake'];
    this.currentTab = tabMapping[index] as 'avg-daily-profit' | 'lost-revenue-by-driver-mistake';
  }

  public openUpdateCostsModal(): void {
    const dialogRef = this.dialog.open(UpdateMonthlyCostsModalComponent, {
      width: '1000px',
      data: {},
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        console.log('Updated Costs:', result);
      }
    });
  }

  public identifyDriver(index: number, driver: any) {
    return driver.id;
  }

  private updateCard(
    index: number,
    value: string,
    indicatorIcon: string,
    indicatorText: string,
    textColor: string,
    indicatorClass: string,
  ): void {
    this.cards[index] = {
      ...this.cards[index],
      value,
      indicatorIcon,
      indicatorText,
      textColor,
      indicatorClass,
    };
  }

  private updateCardError(index: number): void {
    this.cards[index] = {
      ...this.cards[index],
      value: '-',
      indicatorIcon: '',
      indicatorText: '',
      textColor: '',
      indicatorClass: '',
    };
  }

  private updateAdditionalCard(
    index: number,
    value: string,
    indicatorIcon: string,
    indicatorText: string,
    textColor: string,
    indicatorClass: string,
  ): void {
    this.additionalCards[index] = {
      ...this.additionalCards[index],
      value,
      indicatorIcon,
      indicatorText,
      textColor,
      indicatorClass,
    };
  }

  private updateAdditionalCardError(index: number): void {
    this.additionalCards[index] = {
      ...this.additionalCards[index],
      value: '-',
      indicatorIcon: '',
      indicatorText: '',
      textColor: '',
      indicatorClass: '',
    };
  }

  get visibleCards() {
    return this.showAll ? [...this.cards, ...this.additionalCards] : this.cards;
  }

  private formatCentsToDollars(cents: number): string {
    return this.currencyPipe.transform(cents / 100, 'USD', 'symbol', '1.2-2') || '-';
  }
}
